import { ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { getApiErrorDetail } from '@/lib/utils/error.utils';
import { useRecipientActions } from '../hooks/useRecipientActions';
import { inviteFormSchema, type InviteFormValues } from '../schema';

interface ShareProfileDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

const GENERIC_ERROR = 'Could not send the profile link. Please try again.';

/**
 * "Share Your Startup Profile" modal (MYN-41/54). Composes the shared Dialog.
 * Generates a new recipient share link and emails it - the server's `detail`
 * is surfaced directly on failure (e.g. a friendly message when the
 * recipient already has active/pending access), same pattern as
 * `InviteMemberDialog`.
 */
export function ShareProfileDialog({ entityId, open, onClose }: ShareProfileDialogProps) {
  const { add } = useRecipientActions(entityId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({ resolver: zodResolver(inviteFormSchema) });

  const close = () => {
    reset();
    add.reset();
    onClose();
  };

  const onSubmit = handleSubmit(({ fullName, email }) => {
    add.mutate({ email, recipientName: fullName }, { onSuccess: close });
  });

  const submitError = add.isError ? (getApiErrorDetail(add.error) ?? GENERIC_ERROR) : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title="Share Your Startup Profile"
      titleClassName="text-lg font-bold text-navy"
      description="Enter the name and email address of the person you'd like to share your startup profile with. They will receive a secure link to view your profile."
      showCloseButton
      footer={
        <Button
          type="submit"
          form="share-profile-form"
          variant="green"
          disabled={add.isPending}
          className="rounded-xl! inline-flex items-center justify-center gap-2 px-6 py-3 text-sm"
        >
          {add.isPending ? 'Sending...' : 'Send Profile Link'}
          <ArrowRight size={16} aria-hidden="true" />
        </Button>
      }
    >
      <form id="share-profile-form" onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
        <Input
          label="Recipient name"
          placeholder="Jordan Lee"
          {...register('fullName')}
          error={errors.fullName?.message}
        />
        <Input
          label="Recipient email"
          type="email"
          placeholder="recipient@email.com"
          {...register('email')}
          error={errors.email?.message ?? submitError}
        />
        <p className="text-xs text-slate">
          Recipients will be able to view your profile. You can revoke access at any time from
          the Shared Recipients section below.
        </p>
      </form>
    </Dialog>
  );
}
